import app from 'flarum/forum/app';

/**
 * Tipos y llamadas de las fichas de encuentro PASADO (D6/D6b) —
 * PKG-ENCUENTROS-GALERIA.
 *
 * Espejan lo que devuelve `Umbral\Comunidad\Api\MeetupPresenter`. Un solo fichero para las dos
 * paginas (`EncuentrosPage`, `EncuentroPage`) y para el panel: la lista de recursos de T12 ya
 * documento que la misma estructura escrita dos veces se desincroniza en la primera edicion.
 *
 * NO SE USA `app.store`. El payload no es JSON:API —es un sobre propio con `meetups`, `likes` y
 * `comments` juntos— y meterlo en la tienda obligaria a inventar tipos de recurso y relaciones
 * para algo que ninguna otra pantalla consume. Mismo criterio que `AccessCodesSection` y
 * `OverviewSection` del panel.
 */

export type MeetupPhoto = {
  id: number;
  url: string;
  caption: string | null;
  width: number | null;
  height: number | null;
  position: number;
};

export type MeetupCard = {
  id: number;
  slug: string;
  title: string;
  heldOn: string | null;
  place: string | null;
  excerpt: string;
  cover: MeetupPhoto | null;
  photoCount: number;
  likeCount: number;
  liked: boolean;
  commentCount: number;
  published: boolean;
  publishedAt: string | null;
};

/**
 * EL CUERPO DE LA FICHA LLEGA YA ANALIZADO (PKG-FORO-ENCUENTROS-POLISH).
 *
 * El markdown del cuerpo lo interpreta `Umbral\Comunidad\MeetupBody` en el servidor y viaja como
 * esta estructura, no como texto ni como HTML:
 *
 *   - COMO TEXTO obligaria a escribir la gramatica otra vez aqui, y entonces la pagina del SPA y
 *     el HTML server-side de `encuentro.blade.php` serian dos interpretaciones del mismo texto
 *     que solo coinciden mientras nadie toque una de las dos. D6 pide justo lo contrario.
 *   - COMO HTML obligaria a `m.trust()`, o sea a confiar en una cadena para meterla en el DOM.
 *     Con esta estructura el render de abajo produce VNODOS: no hay ningun punto del camino en
 *     el que una cadena del autor pueda convertirse en marcado.
 */
export type MeetupSpan =
  | { type: 'text'; value: string }
  | { type: 'break' }
  | { type: 'strong'; spans: MeetupSpan[] }
  | { type: 'em'; spans: MeetupSpan[] }
  | { type: 'link'; href: string; spans: MeetupSpan[] };

export type MeetupBlock =
  | { type: 'heading'; level: number; spans: MeetupSpan[] }
  | { type: 'paragraph'; spans: MeetupSpan[] }
  | { type: 'list'; ordered: boolean; items: MeetupSpan[][] };

export type MeetupCommentAuthor = {
  id: number;
  username: string;
  displayName: string;
  avatarUrl: string | null;
};

export type MeetupComment = {
  id: number;
  content: string;
  createdAt: string | null;
  /** `null` = la cuenta se borro. Se pinta «—», nunca un nombre inventado. */
  author: MeetupCommentAuthor | null;
};

export type MeetupLikes = { count: number; mine: boolean; can: boolean };

export type MeetupComments = {
  count: number;
  /** Falso para todo el que no sea de n8n Chile: entonces `items` llega VACIO desde el servidor. */
  canRead: boolean;
  canWrite: boolean;
  items: MeetupComment[];
};

export type Meetup = {
  id: number;
  slug: string;
  title: string;
  heldOn: string | null;
  place: string | null;
  /** El markdown tal cual se escribio. Lo usa el panel; esta pantalla pinta `bodyBlocks`. */
  body: string;
  /** Opcional a proposito: un payload servido por una version anterior no lo trae. */
  bodyBlocks?: MeetupBlock[];
  published: boolean;
  publishedAt: string | null;
  photos: MeetupPhoto[];
  likes: MeetupLikes;
  comments: MeetupComments;
  canManage: boolean;
};

export type MeetupList = {
  meetups: MeetupCard[];
  total: number;
  offset: number;
  limit: number;
  canManage: boolean;
};

/** Codigo del 403 de comentar. El cliente lo convierte en la puerta de D6, no en una alerta. */
export const COMMENT_FORBIDDEN = 'umbral_meetup_entry_comment_forbidden';

const MONTHS = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
];

/**
 * `2026-07-12` -> «12 de julio de 2026».
 *
 * SE PARTE LA CADENA, NO SE CONSTRUYE UN `Date`. `new Date('2026-07-12')` se interpreta como
 * medianoche UTC, y en Chile (UTC-3/-4) eso es el 11 por la tarde: la ficha de un encuentro
 * mostraria el dia anterior a media comunidad. Aqui no hay huso que aplicar porque nunca hay
 * instante — es una fecha de calendario y se trata como tres numeros.
 */
export function formatMeetupDate(value: string | null): string {
  if (!value) return '';

  const parts = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);

  if (!parts) return value;

  const month = MONTHS[Number(parts[2]) - 1];

  if (!month) return value;

  return `${Number(parts[3])} de ${month} de ${Number(parts[1])}`;
}

/** Ruta publica de una ficha. Un solo sitio donde se arma. */
export function meetupUrl(slug: string): string {
  return app.route('umbral-comunidad.encuentro', { slug });
}

export function fetchMeetups(params: { offset?: number; status?: string } = {}): Promise<MeetupList> {
  const query: Record<string, any> = {};

  if (params.offset) query['page[offset]'] = params.offset;
  if (params.status) query['filter[status]'] = params.status;

  return app.request<MeetupList>({
    method: 'GET',
    url: app.forum.attribute('apiUrl') + '/umbral/meetups',
    params: query,
  });
}

export function fetchMeetup(idOrSlug: string | number): Promise<{ meetup: Meetup }> {
  return app.request<{ meetup: Meetup }>({
    method: 'GET',
    url: app.forum.attribute('apiUrl') + '/umbral/meetups/' + encodeURIComponent(String(idOrSlug)),
  });
}

export function toggleMeetupLike(idOrSlug: string | number, like: boolean): Promise<{ likes: MeetupLikes }> {
  return app.request<{ likes: MeetupLikes }>({
    method: like ? 'POST' : 'DELETE',
    url: app.forum.attribute('apiUrl') + '/umbral/meetups/' + encodeURIComponent(String(idOrSlug)) + '/like',
  });
}

export function postMeetupComment(
  idOrSlug: string | number,
  content: string
): Promise<{ comment: MeetupComment; count: number }> {
  return app.request<{ comment: MeetupComment; count: number }>({
    method: 'POST',
    url: app.forum.attribute('apiUrl') + '/umbral/meetups/' + encodeURIComponent(String(idOrSlug)) + '/comments',
    body: { data: { attributes: { content } } },
  });
}

export function deleteMeetupComment(
  idOrSlug: string | number,
  commentId: number
): Promise<{ deleted: boolean; count: number }> {
  return app.request<{ deleted: boolean; count: number }>({
    method: 'DELETE',
    url:
      app.forum.attribute('apiUrl') +
      '/umbral/meetups/' +
      encodeURIComponent(String(idOrSlug)) +
      '/comments/' +
      encodeURIComponent(String(commentId)),
  });
}
